import { ReactElement, useState } from "react";
import logo from "../assets/BudgetTrace Logomark_V2.svg";
import { usePageScroll } from "../hooks/usePageScroll";
import accountImage from "../assets/account.png";
import activityImage from "../assets/activeity.png";
import analyticsImage from "../assets/analystic.png";
import {
  Box,
  Button,
  Typography,
  Stack,
  Paper,
  Card,
  CardContent,
  CardMedia,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import GroupIcon from "@mui/icons-material/Group";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import SecurityIcon from "@mui/icons-material/Security";
import EmailIcon from "@mui/icons-material/Email";

interface LandingPageProps {
  onLoginClick: () => void;
  onDemoClick?: () => void;
}

function LandingPage({ onLoginClick, onDemoClick }: LandingPageProps): ReactElement {
  const [selectedPreview, setSelectedPreview] = useState<"accounts" | "activity" | "analytics">("accounts");
  const { ref: mainRef, onWheel } = usePageScroll();

  const dashboardPreviews = [
    {
      id: "accounts" as const,
      title: "Accounts Management",
      image: accountImage,
      description: "Manage all your financial accounts in one place with support for multiple currencies.",
      details: [
        "Create and organize multiple accounts",
        "Support for various account types (Bank, Cash, Credit, etc.)",
        "Multi-currency support with automatic exchange rates",
        "Track balances and account history",
        "Quick account switching and filtering",
      ],
    },
    {
      id: "activity" as const,
      title: "Activity & Transactions",
      image: activityImage,
      description: "Track and manage all your expenses and income transactions with detailed filtering and categorization.",
      details: [
        "Record income and expense transactions",
        "Advanced filtering by category, account, user, and date",
        "Real-time transaction updates",
        "Duplicate transaction detection",
        "Quick edit and delete functionality",
        "Transaction history and search",
      ],
    },
    {
      id: "analytics" as const,
      title: "Analytics & Insights",
      image: analyticsImage,
      description: "Visualize your financial data with comprehensive analytics, charts, and spending insights.",
      details: [
        "Interactive charts and graphs",
        "Spending breakdown by category",
        "Income vs expense analysis",
        "Trend analysis over time",
        "Account-wise financial overview",
        "Quick navigation to related transactions",
      ],
    },
  ];

  const features = [
    {
      icon: <ReceiptIcon sx={{ fontSize: 40, color: "primary.main" }} />,
      title: "Track Expenses",
      description: "Record and categorize all your transactions with ease. Stay on top of your spending with detailed expense tracking.",
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 40, color: "primary.main" }} />,
      title: "Smart Analytics",
      description: "Visualize your financial data with insightful charts and reports. Understand your spending patterns at a glance.",
    },
    {
      icon: <AccountBalanceWalletIcon sx={{ fontSize: 40, color: "primary.main" }} />,
      title: "Multiple Accounts",
      description: "Manage multiple accounts and currencies. Keep all your financial information organized in one place.",
    },
    {
      icon: <GroupIcon sx={{ fontSize: 40, color: "primary.main" }} />,
      title: "Team Collaboration",
      description: "Perfect for small teams. Share workspaces, track shared expenses, and collaborate seamlessly.",
    },
    {
      icon: <UpgradeIcon sx={{ fontSize: 40, color: "primary.main" }} />,
      title: "Future-Ready",
      description: "Built with scalability in mind. Designed to grow with your needs, from personal use to team collaboration.",
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: "primary.main" }} />,
      title: "Secure & Private",
      description: "Your data is secure and private. We use industry-standard security practices to protect your financial information.",
    },
  ];

  return (
    <Box
      ref={mainRef}
      component="main"
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        position: "relative",
        overflow: "visible",
      }}
      onWheel={onWheel}
    >
      {/* Header */}
      <Box
        component="header"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1100,
          py: { xs: 0.5, md: 0.625 },
          px: { xs: 2, sm: 3, md: 4, lg: 6, xl: 8 },
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          flexShrink: 0,
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: "auto", width: "100%" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                component="img"
                src={logo}
                alt="Budget-Trace"
                sx={{
                  width: { xs: 64, sm: 80 },
                  height: "auto",
                }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                Budget-Trace
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="outlined"
                onClick={onDemoClick || onLoginClick}
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: 1.5,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                Try Demo
              </Button>
              <Button
                variant="contained"
                onClick={onLoginClick}
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: 1.5,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                Login
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>

      {/* Hero Section */}
      <Box 
        component="section"
        sx={{ 
          flexGrow: 1, 
          pt: { xs: 2, md: 3 }, 
          pb: { xs: 6, md: 10 },
          px: { xs: 2, sm: 3, md: 4, lg: 6, xl: 8 },
          width: "100%",
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: "auto", width: "100%" }}>
          <Stack spacing={3} alignItems="center" sx={{ textAlign: "center", width: "100%" }}>
            {/* Main Hero Content */}
            <Stack spacing={2} alignItems="center" sx={{ maxWidth: { xs: "100%", md: 800, lg: 900 }, width: "100%" }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem", lg: "2.5rem" },
                fontWeight: 700,
                color: "text.primary",
                lineHeight: 1.3,
                mb: 1.5,
              }}
            >
              Track Your Expenses with Clarity and Control
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "0.9375rem", sm: "1rem", md: "1.0625rem" },
                color: "text.secondary",
                maxWidth: { xs: "100%", sm: 600, md: 700 },
                width: "100%",
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              Budget-Trace is a personal & small-team expense tracking app designed
              for clarity, control, and future scalability. Take control of your finances with powerful yet simple tools.
            </Typography>
          </Stack>

          {/* Dashboard Preview Section */}
          <Box
            sx={{
              width: "100%",
              mt: 8,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h3"
              sx={{
                mb: 5,
                fontWeight: 600,
                fontSize: { xs: "1.25rem", md: "1.5rem", lg: "1.75rem" },
                color: "text.primary",
                textAlign: "center",
              }}
            >
              Dashboard Previews
            </Typography>

            {/* Feature Cards */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={3}
              sx={{ mb: 4, width: "100%", maxWidth: 1400 }}
            >
              {dashboardPreviews.map((preview) => (
                <Box
                  key={preview.id}
                  sx={{
                    flex: { xs: "1 1 100%", sm: "1 1 33.333%" },
                    maxWidth: { xs: "100%", sm: "calc(33.333% - 16px)" },
                  }}
                >
                  <Card
                    onClick={() => setSelectedPreview(preview.id)}
                    sx={{
                      cursor: "pointer",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      border: selectedPreview === preview.id ? 3 : 1.5,
                      borderColor:
                        selectedPreview === preview.id
                          ? "primary.main"
                          : "divider",
                      transition: "all 0.3s ease",
                      boxShadow: selectedPreview === preview.id ? 4 : 1,
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 6,
                        borderColor: "primary.main",
                        borderWidth: 2,
                      },
                    }}
                  >
                    {/* Image Section - More Visible */}
                    <Box
                      sx={{
                        width: "100%",
                        height: { xs: 220, sm: 260, md: 280 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(82, 144, 255, 0.04)",
                        p: 2,
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        component="img"
                        src={preview.image}
                        alt={preview.title}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          objectPosition: "center",
                        }}
                      />
                    </Box>
                    <CardContent sx={{ p: 3, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: "1rem", sm: "1.125rem" },
                          color: "text.primary",
                          mb: 1.5,
                        }}
                      >
                        {preview.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                          lineHeight: 1.6,
                          mb: 2,
                          flexGrow: 1,
                        }}
                      >
                        {preview.description}
                      </Typography>
                      {selectedPreview === preview.id && (
                        <Chip
                          label="Selected"
                          color="primary"
                          size="small"
                          sx={{ mt: "auto", fontWeight: 500, alignSelf: "flex-start" }}
                        />
                      )}
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Stack>

            {/* Detailed Preview Display */}
            <Paper
              elevation={2}
              sx={{
                width: "100%",
                minHeight: { xs: 600, md: 700, lg: 800 },
                p: { xs: 4, md: 6, lg: 7 },
                border: 1,
                borderColor: "divider",
                bgcolor: "background.paper",
                borderRadius: 3,
              }}
            >
              {dashboardPreviews
                .filter((preview) => preview.id === selectedPreview)
                .map((preview) => (
                  <Box key={preview.id}>
                    <Stack
                      direction="column"
                      spacing={5}
                      alignItems="stretch"
                    >
                      {/* Details Section */}
                      <Box
                        sx={{
                          width: "100%",
                        }}
                      >
                        <Typography
                          variant="h2"
                          sx={{
                            mb: 3,
                            fontWeight: 600,
                            fontSize: { xs: "1.5rem", md: "1.75rem", lg: "2rem" },
                            color: "text.primary",
                          }}
                        >
                          {preview.title}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            mb: 3,
                            color: "text.secondary",
                            fontSize: { xs: "0.9375rem", sm: "1rem", md: "1.0625rem" },
                            lineHeight: 1.6,
                            fontWeight: 400,
                          }}
                        >
                          {preview.description}
                        </Typography>
                      </Box>

                      {/* Image Section - Below Text */}
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mt: 2,
                        }}
                      >
                        <Box
                          component="img"
                          src={preview.image}
                          alt={preview.title}
                          sx={{
                            width: "100%",
                            maxWidth: { xs: "100%", sm: 600, md: 700, lg: 800 },
                            height: "auto",
                            objectFit: "contain",
                            borderRadius: 2,
                            boxShadow: 3,
                          }}
                        />
                      </Box>
                    </Stack>
                  </Box>
                ))}
            </Paper>

            {/* Tab Navigation (Alternative) */}
            <Box sx={{ mt: 4, width: "100%", maxWidth: 600 }}>
              <Tabs
                value={selectedPreview}
                onChange={(_, newValue) =>
                  setSelectedPreview(newValue as typeof selectedPreview)
                }
                variant="fullWidth"
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "1rem",
                  },
                }}
              >
                {dashboardPreviews.map((preview) => (
                  <Tab
                    key={preview.id}
                    label={preview.title}
                    value={preview.id}
                  />
                ))}
              </Tabs>
            </Box>

            {/* Call to Action Buttons */}
            <Box sx={{ mt: 8, mb: 4, width: "100%" }}>
              <Stack
                spacing={3}
                alignItems="center"
                sx={{ textAlign: "center" }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "1.25rem", md: "1.5rem" },
                    color: "text.primary",
                    mb: 2,
                  }}
                >
                  Ready to Get Started?
                </Typography>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  sx={{ width: "100%", maxWidth: 500 }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    onClick={onLoginClick}
                    sx={{
                      px: 5,
                      py: 2,
                      fontSize: { xs: "1rem", sm: "1.125rem" },
                      fontWeight: 600,
                      flex: { xs: "1 1 100%", sm: "1 1 50%" },
                    }}
                  >
                    Get Started Free
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={onLoginClick}
                    sx={{
                      px: 5,
                      py: 2,
                      fontSize: { xs: "1rem", sm: "1.125rem" },
                      fontWeight: 600,
                      flex: { xs: "1 1 100%", sm: "1 1 50%" },
                    }}
                  >
                    Sign In
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Stack>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          py: { xs: 2.5, md: 3 },
          px: { xs: 2, sm: 3, md: 4, lg: 6, xl: 8 },
          mt: { xs: 4, md: 6 },
          flexShrink: 0,
          pointerEvents: "auto",
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: "auto", width: "100%" }}>
          <Stack spacing={2} direction={{ xs: "column", sm: "row" }} sx={{ alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between" }}>
            {/* Contact Information */}
            <Stack spacing={1.5} sx={{ flex: { xs: "1 1 100%", sm: "1 1 40%" } }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  color: "text.primary",
                }}
              >
                Contact
              </Typography>
              <Stack direction="column" spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <EmailIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography
                    variant="caption"
                    component="a"
                    href="mailto:contact@budgettrace.com"
                    sx={{
                      color: "primary.main",
                      textDecoration: "none",
                      fontSize: "0.75rem",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    contact@budgettrace.com
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <EmailIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography
                    variant="caption"
                    component="a"
                    href="mailto:vahagn@budgettrace.com"
                    sx={{
                      color: "primary.main",
                      textDecoration: "none",
                      fontSize: "0.75rem",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    vahagn@budgettrace.com
                  </Typography>
                </Stack>
              </Stack>
            </Stack>

            {/* Privacy & Permissions - Compact */}
            <Stack spacing={1.5} sx={{ flex: { xs: "1 1 100%", sm: "1 1 55%" } }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  color: "text.primary",
                }}
              >
                Privacy & Permissions
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.7rem",
                  lineHeight: 1.5,
                }}
              >
                Your financial data is encrypted and stored securely. We do not share your information with third parties. 
                By using Budget-Trace, you grant permission to store and process your data for expense tracking. 
                Workspace owners control member permissions and access levels. Default settings include EUR, USD, GBP currencies 
                and can be customized in workspace settings.
              </Typography>
            </Stack>

            {/* Copyright */}
            <Box
              sx={{
                flex: { xs: "1 1 100%", sm: "0 0 auto" },
                textAlign: { xs: "left", sm: "right" },
                mt: { xs: 2, sm: 0 },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.7rem",
                }}
              >
                © {new Date().getFullYear()} Budget-Trace
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

export default LandingPage;
